/**
 * Utility functions for Markdown processing
 */

/**
 * Detects if content contains Markdown syntax
 * @param content - The content to check
 * @returns true if content contains Markdown patterns
 */
export const isMarkdown = (content: string): boolean => {
  const markdownPatterns = [
    /^#{1,6}\s+/m,           // Headers
    /\*\*.*?\*\*/,           // Bold
    /\*.*?\*/,               // Italic
    /^\s*[-*+]\s+/m,         // Unordered lists
    /^\s*\d+\.\s+/m,         // Ordered lists
    /^\s*>\s+/m,             // Blockquotes
    /\[.*?\]\(.*?\)/,        // Links
    /`.*?`/,                 // Inline code
    /```[\s\S]*?```/         // Code blocks
  ];
  
  return markdownPatterns.some(pattern => pattern.test(content));
};

/**
 * Colors matching the app's markdown (Tailwind hex / theme)
 * Used so pasted HTML looks the same as in-app.
 */
const COPY_STYLES = {
  textDark: '#2D2D2D',
  borderGray: '#d1d5db',
  theadBg: '#f3f4f6',
  blockquoteBg: '#f9fafb',
  codeBg: '#f3f4f6',
  linkBlue: '#2563eb',
} as const;

/**
 * Injects inline styles and a style block so pasted HTML keeps the same colors and borders.
 * Clipboard targets ignore Tailwind classes, so we add inline styles and a <style> block.
 */
export const htmlWithTableBorders = (html: string): string => {
  const { textDark, borderGray, theadBg, linkBlue } = COPY_STYLES;
  const tableStyle = `border-collapse:collapse;border:1px solid ${borderGray};color:${textDark}; padding-bottom: 10px;`;
  // Use background-color (not background shorthand): html-to-pdfmake maps background-color on TH/TD to
  // table cell fillColor. The shorthand becomes a pdfmake "background" on text inlines and can render as a solid black box.
  const thStyle = `border:1px solid ${borderGray};padding:6px 8px;background-color:${theadBg};color:${textDark};font-weight:bold;text-align:left;`;
  const tdStyle = `border:1px solid ${borderGray};padding:6px 8px;color:${textDark};`;

  let out = html
    .replace(/<table(\s[^>]*)>/gi, (_m, attrs) => `<table${attrs} style="${tableStyle}">`)
    .replace(/<thead(\s[^>]*)>/gi, (_m, attrs) => `<thead${attrs} style="background-color:${theadBg};">`)
    .replace(/<th(\s[^>]*)>/gi, (_m, attrs) => `<th${attrs} style="${thStyle}">`)
    .replace(/<td(\s[^>]*)>/gi, (_m, attrs) => `<td${attrs} style="${tdStyle}">`);

  const styleBlock = `
<style>
  body, p, li, span, div, h1, h2, h3, h4, h5, h6 { color: ${textDark}; }
  .text-hex-text-dark, .text-chat-dark { color: ${textDark}; }
  a { color: ${linkBlue}; text-decoration: underline; }
  table { border-collapse: collapse; border: 1px solid ${borderGray}; color: ${textDark}; padding-bottom: 10px; }
  th, td { border: 1px solid ${borderGray}; padding: 6px 8px; color: ${textDark}; }
  thead { background-color: ${theadBg}; }
  th { font-weight: bold; }
  code { background: ${COPY_STYLES.codeBg}; padding: 2px 4px; border-radius: 4px; }
  pre { background: ${COPY_STYLES.codeBg}; padding: 8px; border-radius: 4px; }
  blockquote { border-left: 4px solid ${borderGray}; background: ${COPY_STYLES.blockquoteBg}; padding: 4px 12px; color: ${textDark}; }
</style>`;
  out = styleBlock + out;
  return out;
};

/**
 * Prepares HTML the same way as copy-to-clipboard (tables + theme colors via inline rules),
 * but strips the embedded &lt;style&gt; block so html-to-pdfmake receives clean markup with
 * selectable text preserved in the PDF pipeline.
 */
export const htmlForPdfFromRenderedHtml = (html: string): string => {
  return htmlWithTableBorders(html).replace(/^\s*<style>[\s\S]*?<\/style>\s*/i, "");
};

/** Matches QuillHelper PDF &lt;hr&gt; replacement tables — do not change their layout. */
const QUILL_HR_EXPORT_LINE_CLASS = "quill-hr-export-line";

/**
 * Merges inline styles so markdown tables use the content width and cells wrap text.
 * Avoids `table-layout: fixed` and forced pdfmake column widths — those hid columns when
 * combined with html-to-pdfmake output.
 */
export const applyChatPdfTableFitStylesToHtml = (html: string): string => {
  if (!html.includes("<table")) return html;
  try {
    const div = document.createElement("div");
    div.innerHTML = html;
    div.querySelectorAll("table").forEach((node) => {
      const table = node as HTMLTableElement;
      if (table.classList.contains(QUILL_HR_EXPORT_LINE_CLASS)) return;

      table.style.width = "100%";
      table.style.maxWidth = "100%";

      table.querySelectorAll("th, td").forEach((cellNode) => {
        const cell = cellNode as HTMLElement;
        cell.style.wordBreak = "break-word";
        cell.style.overflowWrap = "break-word";
      });
    });
    return div.innerHTML;
  } catch {
    return html;
  }
};

/** Clipboard-style table borders without the &lt;style&gt; block, plus PDF page-width table layout. */
export const prepareChatMessageMarkdownHtmlForPdf = (renderedInnerHtml: string): string => {
  return applyChatPdfTableFitStylesToHtml(htmlForPdfFromRenderedHtml(renderedInnerHtml));
};

/**
 * Mutates an export clone for CoreExpander:
 * - Strips clip/absolute from collapsed bodies so tables/markdown layout in pdfmake.
 * - Removes header chevron markup so PDF export does not show a stray ">" / glyph from the SVG.
 */
export const stripCollapsedCoreExpanderLayoutFromExportClone = (
  root: HTMLElement,
): void => {
  root.querySelectorAll("[data-core-expander-collapsed]").forEach((el) => {
    const h = el as HTMLElement;
    h.removeAttribute("data-core-expander-collapsed");
    h.removeAttribute("style");
    h.removeAttribute("aria-hidden");
    h.removeAttribute("inert");
  });

  root.querySelectorAll("[data-core-expander-chevron]").forEach((el) => {
    el.remove();
  });
};

